import type {IntlShape} from 'react-intl';
import {createToast} from '../toasts/another-toast/toaster';
import type {FailureCause, InvokeResult, StopResult} from './novaInvocationStore';
import {RUN_CAP} from './novaInvocationStore';

const TOAST_DURATION = 5000;

// The words for each reason a run never got going. Reused from the failure copy the conversation
// already shows, so a refusal reads the same on a row as it does in the thread.
const CAUSE_MESSAGE_ID: Record<FailureCause, string> = {
	missing_access: 'nova_invocation.failure.cause.missing_access',
	timeout: 'nova_invocation.failure.cause.timeout',
	unavailable_model: 'nova_invocation.failure.cause.unavailable_model',
	permission: 'nova_invocation.failure.cause.permission',
	no_ai_access: 'nova_invocation.failure.cause.no_ai_access',
	document_processing: 'nova_invocation.failure.cause.document_processing',
	at_capacity: 'nova_invocation.failure.cause.at_capacity',
	out_of_credits: 'nova_invocation.control.reason_out_of_credits_no_date',
	task_already_running: 'nova_invocation.failure.cause.task_already_running',
	generic: 'nova_invocation.failure.cause.generic',
};

// What to tell the person about a run, reply, change of agent or retry that did not get going, or
// null where there is nothing to say here: the run left a turn the conversation shows, the
// platform's own error flow already spoke (an expired session, a lost connection), or the person
// stopped it themselves.
export function refusalMessage(result: InvokeResult, formatMessage: IntlShape['formatMessage']): string | null {
	if (result.kind === 'refused') {
		switch (result.reason) {
			case 'already_running':
				return formatMessage({id: CAUSE_MESSAGE_ID.task_already_running});
			case 'at_run_cap':
				return formatMessage({id: 'nova_invocation.control.reason_run_cap'}, {count: RUN_CAP});
			case 'no_agent':
				return formatMessage({id: 'nova_invocation.control.reason_no_agent_available'});
			case 'out_of_credits':
				return formatMessage({id: CAUSE_MESSAGE_ID.out_of_credits});
			default:
				return null;
		}
	}
	if (result.kind !== 'failed' || result.turn || result.replyRecorded || result.reported) return null;
	return formatMessage({id: CAUSE_MESSAGE_ID[result.cause] ?? CAUSE_MESSAGE_ID.generic});
}

// Every refusal says why. Without this the control went from Working back to Action and nothing
// on the page said what had happened. Returns whether it showed one.
export function toastIfRefused(result: InvokeResult, formatMessage: IntlShape['formatMessage']): boolean {
	const message = refusalMessage(result, formatMessage);
	if (!message) return false;
	createToast({duration: TOAST_DURATION, message});
	return true;
}

const STOP_MESSAGE_ID: Record<StopResult, string> = {
	stopped: 'nova_invocation.stop.stopped',
	already_finished: 'nova_invocation.stop.already_finished',
	failed: 'nova_invocation.stop.failed',
};

// What a Stop came to, said once, wherever Stop was pressed.
export function toastStopResult(result: StopResult, formatMessage: IntlShape['formatMessage']): void {
	createToast({duration: TOAST_DURATION, message: formatMessage({id: STOP_MESSAGE_ID[result]})});
}
